import React from 'react'
import { LoggerProvider, TenantMappingProvider, UserContextProvider } from '@vp/ubik-context'
import type { FragmentRootOptions, TenantMapping, TrackedUserContext, UserContext } from '@vp/ubik-fragment-types'
import { withErrorBoundary } from './with-error-boundary'
import { withStrictMode } from './with-strict-mode'

export type RootProvidersProps = {
  /** Normalized root options — the output of `createRootOptions`. */
  rootOptions: FragmentRootOptions
  userContext: UserContext
  /** Array populated by `UserContextProvider` as a side effect of render.
   *  Server render reads it back to build tracking; the client passes `[]`. */
  trackedUserContext: TrackedUserContext[]
  /** When provided, wraps `children` in a `TenantMappingProvider`. */
  tenantMappings?: TenantMapping
  /** The platform-specific Swan subtree (style registry + `SwanProvider`).
   *  Node and Web differ here, so the subtree is supplied by the caller. */
  children: React.ReactNode
}

/**
 * The Ubik provider envelope shared by the Node and Web `createRootProvider`
 * implementations:
 *
 * `ErrorBoundary → StrictMode → LoggerProvider → UserContextProvider → (TenantMappingProvider) → children`
 *
 * The Swan-specific subtree differs between platforms (SSR style-registry vs.
 * client style-registry) and is passed in as `children`.
 */
export const RootProviders = ({ rootOptions, userContext, trackedUserContext, tenantMappings, children }: RootProvidersProps): React.ReactElement => {
  const withTenant = tenantMappings ? <TenantMappingProvider value={tenantMappings}>{children}</TenantMappingProvider> : children
  return withErrorBoundary(
    withStrictMode(
      <LoggerProvider loggerOptions={rootOptions.loggerOptions}>
        <UserContextProvider value={{ userContext, trackedUserContext }}>{withTenant}</UserContextProvider>
      </LoggerProvider>,
      rootOptions.strictMode,
    ),
    rootOptions.errorBoundaryOptions,
  )
}
